/**
 * In-app documentation hub — all help links resolve to /articles routes.
 */

export const ARTICLE_CATEGORIES = [
  'All',
  'Getting started',
  'Business',
  'Developers',
  'Integrations',
  'API',
  'Voice',
  'Architecture',
];

/** @typedef {'markdown' | 'component'} ArticleType */

/**
 * @type {Array<{
 *   id: string;
 *   route: string;
 *   title: string;
 *   description: string;
 *   category: string;
 *   tags: string[];
 *   readTime: string;
 *   type: ArticleType;
 *   markdownPath?: string;
 *   featured?: boolean;
 * }>}
 */
export const ARTICLES_REGISTRY = [
  {
    id: 'business-quickstart',
    route: '/articles/docs/business-quickstart',
    title: 'Quick start — get your first agent live',
    description: 'Sign in, add API keys, create an agent, connect phone, and run a test call.',
    category: 'Getting started',
    tags: ['Quick start', 'Business', 'Setup'],
    readTime: '8 min read',
    type: 'markdown',
    markdownPath: 'business/quickstart.md',
    featured: true,
  },
  {
    id: 'business-agents',
    route: '/articles/docs/business-agents',
    title: 'Creating and managing agents',
    description: 'Templates, voices, prompts, and testing your AI voice agents.',
    category: 'Business',
    tags: ['Agents', 'Voice'],
    readTime: '10 min read',
    type: 'markdown',
    markdownPath: 'business/agents.md',
  },
  {
    id: 'business-integrations',
    route: '/articles/docs/business-integrations',
    title: 'Integrations overview',
    description: 'Phone providers, CRM, Zapier, and agent tools in one place.',
    category: 'Integrations',
    tags: ['Integrations', 'Twilio', 'Zapier'],
    readTime: '7 min read',
    type: 'markdown',
    markdownPath: 'business/integrations.md',
  },
  {
    id: 'business-knowledge-base',
    route: '/articles/docs/business-knowledge-base',
    title: 'Knowledge base for your agent',
    description: 'Upload documents and URLs so your agent answers from your content.',
    category: 'Business',
    tags: ['Knowledge', 'RAG'],
    readTime: '6 min read',
    type: 'markdown',
    markdownPath: 'business/knowledge-base.md',
  },
  {
    id: 'business-call-logs',
    route: '/articles/docs/business-call-logs',
    title: 'Call logs and conversations',
    description: 'Review transcripts, recordings, and call outcomes.',
    category: 'Business',
    tags: ['Call logs', 'Transcripts'],
    readTime: '6 min read',
    type: 'markdown',
    markdownPath: 'business/call-logs.md',
  },
  {
    id: 'business-billing',
    route: '/articles/docs/business-billing',
    title: 'Billing and API keys',
    description: 'Plans, BYOK provider keys, and usage.',
    category: 'Business',
    tags: ['Billing', 'API keys'],
    readTime: '5 min read',
    type: 'markdown',
    markdownPath: 'business/billing-and-keys.md',
  },
  {
    id: 'developers-getting-started',
    route: '/articles/docs/developers-getting-started',
    title: 'Developer getting started',
    description: 'External API v2, authentication, and your first programmatic call.',
    category: 'Developers',
    tags: ['API', 'SDK'],
    readTime: '12 min read',
    type: 'markdown',
    markdownPath: 'developers/getting-started.md',
  },
  {
    id: 'developers-api-reference',
    route: '/articles/docs/developers-api-reference',
    title: 'API reference',
    description: 'Endpoints, headers, and examples for the AariyaTalk API.',
    category: 'Developers',
    tags: ['API', 'Reference'],
    readTime: '15 min read',
    type: 'markdown',
    markdownPath: 'developers/api-reference.md',
  },
  {
    id: 'developers-api-overview',
    route: '/articles/docs/developers-api-overview',
    title: 'API overview & OpenAPI contract',
    description: 'How the API is structured and where to find the OpenAPI spec.',
    category: 'Developers',
    tags: ['OpenAPI', 'Contract'],
    readTime: '8 min read',
    type: 'markdown',
    markdownPath: 'developers/api-overview.md',
  },
  {
    id: 'developers-transcript-streaming',
    route: '/articles/docs/developers-transcript-streaming',
    title: 'Transcript streaming',
    description: 'SSE and JSON long-poll for realtime transcript events.',
    category: 'Developers',
    tags: ['Transcripts', 'SSE'],
    readTime: '10 min read',
    type: 'markdown',
    markdownPath: 'developers/transcript-streaming.md',
  },
  {
    id: 'developers-webhooks',
    route: '/articles/docs/developers-webhooks',
    title: 'Webhooks',
    description: 'Outbound events for calls, transcripts, and integrations.',
    category: 'Developers',
    tags: ['Webhooks', 'Zapier'],
    readTime: '8 min read',
    type: 'markdown',
    markdownPath: 'developers/webhooks.md',
  },
  {
    id: 'mcp-overview',
    route: '/articles/docs/mcp-overview',
    title: 'MCP — agent tools & integrations',
    description: 'Connect MCP servers so agents can use external tools on calls.',
    category: 'Integrations',
    tags: ['MCP', 'Tools'],
    readTime: '7 min read',
    type: 'markdown',
    markdownPath: 'mcp/overview.md',
  },
  {
    id: 'rag-knowledge-base',
    route: '/articles/docs/rag-knowledge-base',
    title: 'RAG knowledge base (technical)',
    description: 'How embeddings and retrieval work under the hood.',
    category: 'Developers',
    tags: ['RAG', 'pgvector'],
    readTime: '10 min read',
    type: 'markdown',
    markdownPath: 'rag/knowledge-base.md',
  },
  {
    id: 'rag-vector-store-choice',
    route: '/articles/docs/rag-vector-store-choice',
    title: 'Vector store choice (pgvector)',
    description: 'Why AariyaTalk keeps Supabase pgvector instead of Pinecone/Qdrant/Chroma.',
    category: 'Developers',
    tags: ['RAG', 'pgvector'],
    readTime: '5 min read',
    type: 'markdown',
    markdownPath: 'rag/vector-store-choice.md',
  },
  {
    id: 'architecture-platform-phases',
    route: '/articles/docs/architecture-platform-phases',
    title: 'Platform roadmap & analytics',
    description: 'Phased platform capabilities and what is available today.',
    category: 'Architecture',
    tags: ['Roadmap', 'Analytics'],
    readTime: '6 min read',
    type: 'markdown',
    markdownPath: 'architecture/platform-phases.md',
  },
  {
    id: 'twilio-integration',
    route: '/articles/twilio-integration',
    title: 'Connecting Your Own Twilio Account with AariyaTalk',
    description:
      'Integrate your Twilio account to use your own phone numbers for AI voice agents.',
    category: 'Integrations',
    tags: ['Twilio', 'Voice', 'Setup'],
    readTime: '15 min read',
    type: 'component',
    featured: false,
  },
  {
    id: 'zapier-transcriptions-webhook',
    route: '/articles/zapier/transcriptions-webhook',
    title: 'Zapier Transcriptions Webhook API',
    description: 'Set up Zapier webhooks for call transcriptions.',
    category: 'API',
    tags: ['Zapier', 'Webhooks', 'Transcriptions'],
    readTime: '10 min read',
    type: 'component',
  },
  {
    id: 'zapier-recordings-webhook',
    route: '/articles/zapier/recordings-webhook',
    title: 'Zapier Recordings Webhook API',
    description: 'Set up Zapier webhooks for call recordings.',
    category: 'API',
    tags: ['Zapier', 'Webhooks', 'Recordings'],
    readTime: '10 min read',
    type: 'component',
  },
  {
    id: 'call-recordings-and-transcripts',
    route: '/articles/call-recordings-and-transcripts',
    title: 'Call Recordings and Transcripts',
    description: 'How call logs, recordings, and transcripts work for VoIP providers.',
    category: 'Voice',
    tags: ['Call Logs', 'Recordings', 'Transcripts'],
    readTime: '8 min read',
    type: 'component',
  },
  {
    id: 'aws-polly-integration',
    route: '/articles/aws-polly-integration',
    title: 'AWS Polly Integration with AariyaTalk',
    description: 'Configure Amazon Polly as your TTS provider.',
    category: 'Integrations',
    tags: ['AWS', 'Polly', 'TTS'],
    readTime: '7 min read',
    type: 'component',
  },
];

const byId = Object.fromEntries(ARTICLES_REGISTRY.map((a) => [a.id, a]));

export function getArticleById(id) {
  return byId[id] || null;
}

export function getArticlePath(id) {
  const article = byId[id];
  return article?.route || '/articles';
}

/** Map relative .md links in synced docs to in-app routes */
export const MARKDOWN_LINK_ALIASES = {
  'agents.md': 'business-agents',
  'integrations.md': 'business-integrations',
  'call-logs.md': 'business-call-logs',
  'knowledge-base.md': 'business-knowledge-base',
  'quickstart.md': 'business-quickstart',
  'getting-started.md': 'developers-getting-started',
  'api-reference.md': 'developers-api-reference',
  'transcript-streaming.md': 'developers-transcript-streaming',
  'webhooks.md': 'developers-webhooks',
  'overview.md': 'mcp-overview',
};
